import React, { useContext, useEffect, useState } from "react";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Pagination from '@mui/material/Pagination';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link } from "react-router-dom";
import AuthContext from "../../context/auth/authContext";
import FriendContext from "../../context/friending/friendContext";

const UserCard = ({ user, actionLabel, actionColor, onAction }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 2, p: 2,
    border: '1px solid', borderColor: 'divider', borderRadius: 2,
  }}>
    <Avatar src={user.avatar?.image_link} sx={{ width: 40, height: 40 }}>
      {(user.first_name || user.user_id || '?')[0].toUpperCase()}
    </Avatar>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {user.name || ((user.first_name || '') + ' ' + (user.last_name || '')).trim() || user.user_id}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        @{user.user_id}
      </Typography>
    </Box>
    {!user.changed && (
      <Button
        variant="outlined" size="small"
        color={actionColor || "primary"}
        onClick={() => onAction(user.user_id)}
        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
      >
        {actionLabel}
      </Button>
    )}
    {user.changed && (
      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        {actionLabel === 'Unfollow' ? 'Unfollowed' : 'Followed'}
      </Typography>
    )}
  </Box>
);

const ManageFollowing = () => {
  const authContext = useContext(AuthContext);
  const { loadUser } = authContext;
  const friendContext = useContext(FriendContext);
  const {
    friends, getFriends, removeFriend, addFriend,
    followers, followersPage, followersTotal, getFollowers,
  } = friendContext;

  const [tab, setTab] = useState(0);

  useEffect(() => {
    loadUser();
    getFriends();
    getFollowers(1);
    // eslint-disable-next-line
  }, []);

  const followerPages = Math.ceil(followersTotal / 20);

  return (
    <Box sx={{ width: '100%', pt: 8, maxWidth: 640 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button component={Link} to="/profile" startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Back to profile
        </Button>
      </Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Your Connections</Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Following (${friends.length})`} sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label={`Followers (${followersTotal})`} sx={{ textTransform: 'none', fontWeight: 600 }} />
      </Tabs>

      {tab === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {friends && friends.length > 0 ? (
            friends.map((f) => (
              <UserCard key={f.user_id} user={f} actionLabel="Unfollow" actionColor="inherit" onAction={removeFriend} />
            ))
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
              You're not following anyone yet.
            </Typography>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {followers && followers.length > 0 ? (
            <>
              {followers.map((f) => (
                <UserCard key={f.user_id} user={f} actionLabel="Follow back" onAction={addFriend} />
              ))}
              {followerPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination count={followerPages} page={followersPage} onChange={(_, p) => getFollowers(p)} color="primary" />
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
              No followers yet.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ManageFollowing;
